import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  BentoCountEntity,
  BentoKatsuEntity,
  ExportDataEntity,
  ExportField,
  KatsuEntityCountsOrBooleans,
  ResultsDataEntity,
} from '@/types/entities';
import { RequestStatus } from '@/types/requests';
import type { DiscoveryResponseOrMessage } from '@/types/discovery/response';
import type { DiscoveryScopeSelection } from '@/features/metadata/metadata.store';
import type {
  FiltersState,
  FtsQueryType,
  SearchFieldResponse,
  DiscoveryMatchObject,
  DiscoveryMatchPhenopacket,
  DiscoveryMatchBiosample,
  DiscoveryMatchExperiment,
  DiscoveryMatchExperimentResult,
  DiscoveryUIHints,
} from '@/features/search/types';
import type { Sections } from '@/types/data';

import { MIN_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { DEFAULT_TEXT_QUERY_TYPE, EMPTY_KATSU_ENTITY_COUNTS } from './constants';

import { discoveryChartProcessingAndLocalStorage } from './discoveryChartProcessingAndLocalStorage';
import { performKatsuDiscovery, STALE_DISCOVERY_REJECTION } from './performKatsuDiscovery.thunk';
import { fetchSearchFields } from './fetchSearchFields.thunk';
import { fetchDiscoveryMatches } from './fetchDiscoveryMatches.thunk';
import { fetchDiscoveryMatchExportFields } from './fetchDiscoveryMatchExportFields.thunk';
import { fetchDiscoveryUIHints } from './fetchDiscoveryUIHints.thunk';
import { bentoKatsuEntityToResultsDataEntity, checkFiltersStatesEqual } from './utils';

export type QueryResultMatchData<T extends DiscoveryMatchObject> = {
  status: RequestStatus;
  page: number;
  totalMatches: number;
  matches: T[] | undefined;
  invalid: boolean;
};

// Maps a selected row's table key to the ID value that should be sent to the batch export endpoints. The two differ
// for the phenopacket/individual table, where the row key is the phenopacket ID but exports need the subject/individual
// ID. The value is undefined if the row doesn't have an exportable ID (e.g., a phenopacket without a linked subject).
export type SelectedRowMap = Record<string, string | undefined>;

export type QueryState = {
  defaultLayout: Sections;
  sections: Sections;
  // -------------------------------------
  fieldsStatus: RequestStatus;
  discoveryStatus: RequestStatus;
  discoveryError: string;
  // ----
  filterSections: SearchFieldResponse['sections'];
  filters: FiltersState;
  // ----
  textQuery: string;
  textQueryType: FtsQueryType;
  // ----
  // Whether the first search has been executed; can't be reset on 'search mode' (filter/text) change the way
  // (filter|text)QueryStatus can. This is instead only reset when the complete query state is reset.
  doneFirstLoad: boolean;
  message: string;

  selectedEntity: BentoCountEntity | null;

  // node-level data cache
  nodeCountsOrBools: KatsuEntityCountsOrBooleans;
  nodeCountsOrBoolsFetched: boolean;

  // results
  resultCountsOrBools: KatsuEntityCountsOrBooleans;
  resultCountsInvalid: boolean;
  resultCountsByDataset: Record<string, KatsuEntityCountsOrBooleans> | undefined;
  pageSize: number;
  matchData: {
    phenopacket: QueryResultMatchData<DiscoveryMatchPhenopacket>;
    biosample: QueryResultMatchData<DiscoveryMatchBiosample>;
    experiment: QueryResultMatchData<DiscoveryMatchExperiment>;
    experiment_result: QueryResultMatchData<DiscoveryMatchExperimentResult>;
  };
  // Rows selected for export, per entity table, keyed by table row key. Persists across pagination since it's
  // independent of which page's matches are currently loaded.
  selectedRows: Record<ResultsDataEntity, SelectedRowMap>;

  // export fields available per entity, cached once fetched
  exportFields: Record<ExportDataEntity, { status: RequestStatus; fields: ExportField[] | undefined }>;

  // UI hints
  uiHints: {
    status: RequestStatus;
    data: DiscoveryUIHints;
  };
};

const INITIAL_MATCH_DATA_STATE = {
  status: RequestStatus.Idle,
  page: 0,
  totalMatches: 0,
  matches: undefined,
  invalid: false,
};

const INITIAL_EXPORT_FIELDS_STATE = { status: RequestStatus.Idle, fields: undefined };

const initialState: QueryState = {
  defaultLayout: [],
  sections: [],
  // -------------------------------------
  fieldsStatus: RequestStatus.Idle,
  discoveryStatus: RequestStatus.Idle,
  discoveryError: '',
  // ----
  filterSections: [],
  filters: {},
  // ----
  textQuery: '',
  textQueryType: DEFAULT_TEXT_QUERY_TYPE,
  // ----
  doneFirstLoad: false,
  message: '',
  // ----
  selectedEntity: null,
  // ----
  nodeCountsOrBools: EMPTY_KATSU_ENTITY_COUNTS,
  nodeCountsOrBoolsFetched: false,
  // ----
  resultCountsOrBools: EMPTY_KATSU_ENTITY_COUNTS,
  resultCountsByDataset: undefined,
  resultCountsInvalid: false,
  pageSize: MIN_PAGE_SIZE,
  matchData: {
    phenopacket: INITIAL_MATCH_DATA_STATE,
    biosample: INITIAL_MATCH_DATA_STATE,
    experiment: INITIAL_MATCH_DATA_STATE,
    experiment_result: INITIAL_MATCH_DATA_STATE,
  },
  selectedRows: {
    phenopacket: {},
    biosample: {},
    experiment: {},
    experiment_result: {},
  },
  // ----
  exportFields: {
    phenopacket: INITIAL_EXPORT_FIELDS_STATE,
    individual: INITIAL_EXPORT_FIELDS_STATE,
    biosample: INITIAL_EXPORT_FIELDS_STATE,
    experiment: INITIAL_EXPORT_FIELDS_STATE,
    experiment_result: INITIAL_EXPORT_FIELDS_STATE,
  },
  // ----
  uiHints: {
    status: RequestStatus.Idle,
    data: {
      entities_with_data: [],
    },
  },
};

/**
 * Helper utility to invalidate all match data when a relevant parameter changes (and naturally invalidates the results,
 * which we need to reflect in the state in order to re-fetch.)
 * @param state - Draft of QueryState
 */
const invalidateMatchData = (state: Draft<QueryState>) => {
  Object.keys(state.matchData).forEach((e) => {
    const entity: ResultsDataEntity = e as ResultsDataEntity;
    if (state.matchData[entity].status === RequestStatus.Idle) return; // Not fetched yet, don't bother invalidating
    state.matchData[entity].invalid = true;
    if (e !== state.selectedEntity) {
      // For all not currently-visible pages, Redux is the source of truth rather than the URL, so reset the
      // current page upon invalidation:
      state.matchData[entity].page = 0;
    }
    // The query changed, so previously-selected rows may no longer be part of the result set - clear selections:
    state.selectedRows[entity] = {};
  });
};

const query = createSlice({
  name: 'query',
  initialState,
  reducers: {
    rearrange: (state, { payload }: PayloadAction<{ section: string; arrangement: string[] }>) => {
      const { section, arrangement } = payload;
      const sectionObj = state.sections.find((e) => e.sectionId === section)!;
      const chartsCopy = [...sectionObj.charts];
      sectionObj.charts = arrangement.map((e) => chartsCopy.find((i) => e === i.id)!);
    },
    disableChart: (state, { payload }: PayloadAction<{ section: string; id: string }>) => {
      const { section, id } = payload;
      state.sections.find((e) => e.sectionId === section)!.charts.find((e) => e.id === id)!.isDisplayed = false;
    },
    setDisplayedCharts: (state, { payload }: PayloadAction<{ section: string; charts: string[] }>) => {
      const { section, charts } = payload;
      state.sections
        .find((e) => e.sectionId === section)!
        .charts.forEach((val, ind, arr) => {
          arr[ind].isDisplayed = charts.includes(val.id);
        });
    },
    setChartWidth: (state, { payload }: PayloadAction<{ section: string; chart: string; width: number }>) => {
      const { section, chart, width } = payload;
      const chartObj = state.sections.find((e) => e.sectionId === section)!.charts.find((c) => c.id === chart)!;
      chartObj.width = width;
    },
    setAllDisplayedCharts: (state, { payload }: PayloadAction<{ section?: string }>) => {
      if (payload.section) {
        state.sections
          .find((e) => e.sectionId === payload.section)!
          .charts.forEach((_, ind, arr) => {
            arr[ind].isDisplayed = true;
          });
      } else {
        state.sections.forEach((section) => {
          section.charts.forEach((_val, ind, arr) => {
            arr[ind].isDisplayed = true;
          });
        });
      }
    },
    hideAllSectionCharts: (state, { payload }: PayloadAction<{ section: string }>) => {
      state.sections
        .find((e) => e.sectionId === payload.section)!
        .charts.forEach((_, ind, arr) => {
          arr[ind].isDisplayed = false;
        });
    },
    resetLayout: (state) => {
      state.sections = state.defaultLayout;
    },
    // -----------------------------------------------------------------------------------------------------------------
    setFilters: (state, { payload }: PayloadAction<FiltersState>) => {
      // Don't update unnecessarily - but there's a difference between a UI state update (possibly with an empty filter,
      // for instance), and state that invalidates results (a new empty filter does not invalidate results).
      // eqStateSet implies eqInvalidate.
      const [eqStateSet, eqInvalidate] = checkFiltersStatesEqual(state.filters, payload);
      if (eqStateSet) return;
      console.debug('setting filters state', payload);
      state.filters = payload;
      if (eqInvalidate) return;
      // search filters have changed; invalidate existing counts and match data pages if necessary:
      state.resultCountsInvalid = true;
      invalidateMatchData(state);
    },
    setTextQuery: (state, { payload }: PayloadAction<string>) => {
      if (state.textQuery === payload) return;
      state.textQuery = payload;
      // text query has changed; invalidate existing counts and match data pages if necessary:
      state.resultCountsInvalid = true;
      invalidateMatchData(state);
    },
    setTextQueryType: (state, { payload }: PayloadAction<FtsQueryType>) => {
      if (state.textQueryType === payload) return;
      state.textQueryType = payload;
      // text query type has changed; invalidate existing counts and match data pages if necessary:
      state.resultCountsInvalid = true;
      invalidateMatchData(state);
    },
    setDoneFirstLoad: (state) => {
      state.doneFirstLoad = true;
    },
    preSeedCounts: (state, { payload }: PayloadAction<KatsuEntityCountsOrBooleans>) => {
      state.resultCountsOrBools = payload;
      state.doneFirstLoad = true;
    },
    setSelectedEntity: (state, { payload }: PayloadAction<BentoCountEntity | null>) => {
      state.selectedEntity = payload;
    },
    setMatchesPage: (state, { payload }: PayloadAction<[BentoKatsuEntity | BentoCountEntity, number]>) => {
      const rdEntity = bentoKatsuEntityToResultsDataEntity(payload[0]);
      const md = state.matchData[rdEntity];
      if (isNaN(payload[1]) || payload[1] < 0) {
        console.error(`setMatchesPage invalid page: ${payload}`);
        return;
      }
      if (md.page === payload[1]) return;
      md.page = payload[1];
      // Invalidate current match data page contents if the page changes:
      md.invalid = md.status !== RequestStatus.Idle;
    },
    setSelectedRows: (state, { payload }: PayloadAction<[ResultsDataEntity, SelectedRowMap]>) => {
      const [entity, map] = payload;
      state.selectedRows[entity] = map;
    },
    setMatchesPageSize: (state, { payload }: PayloadAction<number>) => {
      if (isNaN(payload) || !PAGE_SIZE_OPTIONS.includes(payload)) {
        console.error(`setMatchesPageSize invalid page size: ${payload}`);
        return;
      }
      if (payload === state.pageSize) return state;
      state.pageSize = payload;
      // Page size is a bit special since it's shared state across all match data, so special care has to be taken to
      // reset all pages and invalidate entities' match data if page state changes.
      invalidateMatchData(state);
    },
    resetAllQueryState: (state, { payload }: PayloadAction<{ resetNodeCounts: boolean } | undefined>) =>
      payload?.resetNodeCounts
        ? initialState
        : {
            ...initialState,
            nodeCountsOrBools: state.nodeCountsOrBools,
            nodeCountsOrBoolsFetched: state.nodeCountsOrBoolsFetched,
          },
  },
  extraReducers: (builder) => {
    builder.addCase(performKatsuDiscovery.pending, (state) => {
      state.discoveryStatus = RequestStatus.Pending;
    });
    builder.addCase(
      performKatsuDiscovery.fulfilled,
      (state, { payload: [scope, response] }: PayloadAction<[DiscoveryScopeSelection, DiscoveryResponseOrMessage]>) => {
        state.discoveryStatus = RequestStatus.Fulfilled;
        state.discoveryError = '';
        state.resultCountsInvalid = false;

        if (!response) {
          return;
        }

        if ('message' in response && response.message) {
          state.message = response.message;
        } else {
          state.message = '';
        }

        if ('counts' in response) {
          if (
            ((!scope.scope.project && !scope.scope.dataset) || (scope.fixedProject && scope.fixedDataset)) &&
            !Object.keys(state.filters).length &&
            !state.textQuery.length
          ) {
            // Cache whole-instance counts when no filters are applied. Used for showing counts in the data catalogue.
            state.nodeCountsOrBools = response.counts;
            state.nodeCountsOrBoolsFetched = true;
          }

          // Populate scope / filter results:

          state.resultCountsOrBools = response.counts;

          state.resultCountsByDataset = response.counts_by_dataset;

          // Side effects: saving/loading layout from local storage
          const { defaultLayout, sectionData } = discoveryChartProcessingAndLocalStorage(scope.scope, response);
          state.defaultLayout = defaultLayout;
          state.sections = sectionData;
        }
      }
    );
    builder.addCase(performKatsuDiscovery.rejected, (state, { payload }) => {
      if (payload === STALE_DISCOVERY_REJECTION) {
        // Scope changed while the request was in flight. Reset to Idle so a new search
        // for the correct scope will be triggered rather than storing stale results.
        state.discoveryStatus = RequestStatus.Idle;
        return;
      }
      state.discoveryStatus = RequestStatus.Rejected;
      // maybe a bit counterintuitive, but a rejected status is a "valid" count response insofar as it reflects the
      // query made, and we don't want to attempt a re-fetch.
      state.resultCountsInvalid = false;
      if (typeof payload === 'string') {
        state.discoveryError = payload;
      }
    });
    // -----
    builder.addCase(fetchSearchFields.pending, (state) => {
      state.fieldsStatus = RequestStatus.Pending;
    });
    builder.addCase(fetchSearchFields.fulfilled, (state, { payload }) => {
      state.fieldsStatus = RequestStatus.Fulfilled;
      state.filterSections = payload.sections;
    });
    builder.addCase(fetchSearchFields.rejected, (state) => {
      state.fieldsStatus = RequestStatus.Rejected;
    });
    // -----
    builder.addCase(fetchDiscoveryMatches.pending, (state, { meta }) => {
      state.matchData[bentoKatsuEntityToResultsDataEntity(meta.arg)].status = RequestStatus.Pending;
    });
    builder.addCase(fetchDiscoveryMatches.fulfilled, (state, { meta, payload }) => {
      const entity: ResultsDataEntity = bentoKatsuEntityToResultsDataEntity(meta.arg);
      const md = state.matchData[entity];
      md.status = RequestStatus.Fulfilled;
      md.matches = payload.results;
      md.totalMatches = payload.pagination.total;
      md.invalid = false;
    });
    builder.addCase(fetchDiscoveryMatches.rejected, (state, { meta }) => {
      state.matchData[bentoKatsuEntityToResultsDataEntity(meta.arg)].status = RequestStatus.Rejected;
    });
    // -----
    builder.addCase(fetchDiscoveryMatchExportFields.pending, (state, { meta }) => {
      state.exportFields[meta.arg].status = RequestStatus.Pending;
    });
    builder.addCase(fetchDiscoveryMatchExportFields.fulfilled, (state, { meta, payload }) => {
      state.exportFields[meta.arg].status = RequestStatus.Fulfilled;
      state.exportFields[meta.arg].fields = payload;
    });
    builder.addCase(fetchDiscoveryMatchExportFields.rejected, (state, { meta }) => {
      state.exportFields[meta.arg].status = RequestStatus.Rejected;
    });
    // -----
    builder.addCase(fetchDiscoveryUIHints.pending, (state) => {
      state.uiHints.status = RequestStatus.Pending;
    });
    builder.addCase(fetchDiscoveryUIHints.fulfilled, (state, { payload }) => {
      state.uiHints.status = RequestStatus.Fulfilled;
      state.uiHints.data = payload;
    });
    builder.addCase(fetchDiscoveryUIHints.rejected, (state) => {
      state.uiHints.status = RequestStatus.Rejected;
    });
  },
});

export const {
  rearrange,
  disableChart,
  setDisplayedCharts,
  setChartWidth,
  setAllDisplayedCharts,
  hideAllSectionCharts,
  resetLayout,
  // ------------------------------------------------------------------
  setFilters,
  setTextQuery,
  setTextQueryType,
  setDoneFirstLoad,
  preSeedCounts,
  setSelectedEntity,
  setMatchesPage,
  setMatchesPageSize,
  setSelectedRows,
  resetAllQueryState,
} = query.actions;
export { performKatsuDiscovery, fetchSearchFields, fetchDiscoveryMatchExportFields, fetchDiscoveryUIHints };
export default query.reducer;
